import { registerOverlay } from 'klinecharts'
import { isNum, type Coord, type OverlayFigure } from './overlayUtils'
import { NO_DEFAULT_FIGURES, registerOnce, type OverlayTemplateParam } from './registrationUtils'

/**
 * Replay loop range - the highlight behind the replay strip's loop button.
 *
 * Two clicks pick the range, like the measure tool: the first click plants a
 * dashed start edge, and while placing the second the end edge + a fill
 * between them preview the range being selected. Once the selection COMPLETES
 * and the loop runs, the picture changes deliberately (user feedback
 * 2026-08-29): only the start edge stays, with a wash from it to the pane's
 * right edge - the end line does not sit well over a playing chart, so the
 * loop reads as "from here onward" even though playback still wraps at the
 * clicked end.
 *
 * Unlike the range tools it is EPHEMERAL and INERT: never persisted or
 * synced, no draggable anchors - the only way to change the range is to
 * toggle the loop off and pick a new one (ChartManager owns that lifecycle;
 * see `toggleReplayLoop`).
 */

export const REPLAY_LOOP_RANGE_NAME = 'replayLoopRange'

// Concrete colors (overlays paint to canvas, so they can't read CSS vars).
// The range-tool blue: this IS a range selection, so it reads as one.
const LOOP_EDGE = '#2962ff'
const LOOP_FILL = 'rgba(41, 98, 255, 0.10)'
const LABEL_TEXT = '#ffffff'

function edge(px: number, height: number): OverlayFigure {
  return {
    type: 'line',
    ignoreEvent: true,
    attrs: {
      coordinates: [
        { x: px, y: 0 },
        { x: px, y: height },
      ],
    },
    styles: { color: LOOP_EDGE, size: 1, style: 'dashed', dashedValue: [4, 4] },
  }
}

function wash(x: number, width: number, height: number): OverlayFigure {
  return {
    type: 'rect',
    ignoreEvent: true,
    attrs: { x, y: 0, width: Math.max(width, 1), height },
    styles: { style: 'fill', color: LOOP_FILL },
  }
}

/**
 * `drawing` = the selection is still being placed (klinecharts `currentStep`
 * is a positive step; finished overlays carry the -1 sentinel).
 *
 *  - Drawing, one point: the start edge tracks the click.
 *  - Drawing, two points (second may be the hover preview): both edges + the
 *    fill between them - the measure-style range preview.
 *  - Finished (looping): the start edge + a wash to the pane's right edge +
 *    the "Loop" tag. NO end edge. A start scrolled off-screen LEFT washes the
 *    whole visible pane - everything on screen is inside the loop, so that is
 *    the correct picture, not an artifact.
 */
export function buildReplayLoopFigures(
  coordinates: Array<Partial<Coord>>,
  bounds: { width: number; height: number },
  drawing: boolean,
): OverlayFigure[] {
  const xs = coordinates.filter((c): c is Coord => isNum(c?.x)).map((c) => c.x)
  if (xs.length === 0) return []

  if (drawing) {
    if (xs.length < 2) return [edge(xs[0], bounds.height)]
    const left = Math.min(xs[0], xs[1])
    const right = Math.max(xs[0], xs[1])
    return [
      wash(left, right - left, bounds.height),
      edge(left, bounds.height),
      edge(right, bounds.height),
    ]
  }

  // The start is the LEFT click - same min() normalization as startLoop.
  const x = Math.min(...xs)
  // Start scrolled past the pane's right edge: nothing of the loop is visible.
  if (x > bounds.width) return []
  return [
    wash(x, bounds.width - x, bounds.height),
    edge(x, bounds.height),
    {
      type: 'text',
      ignoreEvent: true,
      attrs: {
        x: x + 6,
        y: 4,
        text: 'Loop',
        align: 'left',
        baseline: 'top',
      },
      styles: {
        style: 'fill',
        color: LABEL_TEXT,
        size: 11,
        backgroundColor: LOOP_EDGE,
        paddingLeft: 5,
        paddingRight: 5,
        paddingTop: 2,
        paddingBottom: 2,
        borderRadius: 3,
      },
    },
  ]
}

/** Register the `replayLoopRange` overlay template with klinecharts (once). */
export const registerReplayLoopOverlay = registerOnce(() => {
  registerOverlay({
    name: REPLAY_LOOP_RANGE_NAME,
    totalStep: 3, // two clicks (points + 1)
    lock: true, // inert once placed - no drag, no resize
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      const bounds =
        bounding && typeof bounding.width === 'number' && typeof bounding.height === 'number'
          ? { width: bounding.width, height: bounding.height }
          : { width: 10_000, height: 10_000 }
      // Positive = mid-draw; klinecharts stamps finished overlays with -1
      // (OVERLAY_DRAW_STEP_FINISHED). Missing (mocked charts) = finished.
      const step = (overlay as { currentStep?: number }).currentStep
      const drawing = typeof step === 'number' && step > 0
      return buildReplayLoopFigures(coordinates as Array<Partial<Coord>>, bounds, drawing)
    },
  } as OverlayTemplateParam)
})
